import React, {useEffect, useMemo, useState} from 'react';
import {Popup, Position, ToolbarItem} from "devextreme-react/popup";
import {Button, DateBox, NumberBox, TextBox} from "devextreme-react";
import useApi from "../../hooks/useApi";
import ITrancheAge from "../../models/ITrancheAge";
import {add, compareDesc, differenceInDays} from "date-fns";
import {useCalendrierProps} from "./calendrier";
import useLookUpBase from "../../components/selectbox/LookupBase";
import {SelectBox} from "devextreme-react/select-box";
import Guid from "../../models/Guid";
import {IAjoutReservationOption} from "../gestion-reservations/gestion-reservations";
import {useQuery} from "@tanstack/react-query";

;
import IParametreReservationEnLigne from "../../models/IParametreReservationEnLigne";
import {ValueChangedEvent} from "devextreme/ui/number_box"
import {ICellDateRange} from "./calendrier-interfaces";

interface IPopupReserverProps {
    isPopupOpen: boolean,
    onClose: () => void
    onReserver: (ajoutReservationOption: IAjoutReservationOption) => void
}

function TrancheAge({trancheAge, onChange}: { trancheAge: ITrancheAge, onChange: (e: ValueChangedEvent) => void }) {
    return <>
        <div
            style={{gridColumn: 1}}>{`${trancheAge.isAdulte ? "Adultes " : "Enfant"} de ${trancheAge.ageDebut} ${trancheAge.ageFin >= 99 ? "et plus" : ` à ${trancheAge.ageFin} ans`}: `}</div>
        <NumberBox min={trancheAge.isAdulte ? 1 : 0} value={trancheAge.nombrePersonnes || 0} style={{gridColumn: 2}}
                   onValueChanged={onChange}/>
    </>;
}

function isICellDateRange(object: any): object is ICellDateRange {
    return 'debut' in object && 'fin' in object;
}

function PopupReserver({isPopupOpen, onClose, onReserver}: IPopupReserverProps) {
    const {getData: getTrancheAges} = useApi<ITrancheAge[]>("trancheAge");
    const {data: trancheAges} = useQuery({
        queryKey: ['ITrancheAge'],
        queryFn: () => getTrancheAges({customPath: "trancheAge/getasync"})
    });
    const {selectedCell} = useCalendrierProps();
    const selectedDate = selectedCell.find(f => true)?.dates.find(f => true) || new Date();
    const selectedCellSorted = selectedCell.sort((a, b) => compareDesc(b.dates[0].debut, a.dates[0].debut) && compareDesc(b.dates[0].fin, a.dates[0].fin));
    const [selectedTrancheAges, setSelectedTrancheAges] = useState<ITrancheAge[]>();
    const [dateDebut, setDateDebut] = useState<Date>((): Date => {
        if (selectedCellSorted && selectedCellSorted.length > 0 && selectedCellSorted[0].dates && selectedCellSorted[0].dates.length > 0)
            return selectedCellSorted[0]?.dates[0]?.debut;
        if (selectedDate && selectedDate instanceof Date)
            return selectedDate

        if (selectedDate && isICellDateRange(selectedDate))
            return selectedDate.debut

        return new Date();
    })
    const [dateFin, setDateFin] = useState<Date>((): Date => {
        if (selectedCellSorted.length > 0 && selectedCellSorted[selectedCellSorted.length - 1].dates?.length > 0)
            return add(selectedCellSorted[selectedCellSorted.length - 1].dates[0].fin || selectedDate, {days: 1});
        return new Date();
    })
    const [nombreNuitee, setNombreNuitee] = useState<number>(differenceInDays(dateFin, dateDebut))
    const {
        data: equipement,
        refreshData: refreshEquipement
    } = useLookUpBase({
        controllerName: `unite/${selectedCellSorted[0].idUnite}/lookUpEquipements`,
        cacheKey: `lookup-typeequipement-${selectedCellSorted[0].idUnite}`,
        path: "",
        showClear: false
    });
    const [selectedIdEquipement, setSelectedIdEquipement] = useState<Guid>(Guid.empty)
    const [longueurEquipement, setLongueurEquipement] = useState(0)
    const [nombreAnimaux, setNombreAnimaux] = useState(0);
    const {
        data: animaux,
        getData: getAnimaux
    } = useApi<IParametreReservationEnLigne[]>("ParametresReservationEnLigne/animaux");
    useEffect(() => {
        getAnimaux();
    }, []);
    useEffect(() => {
        if (!trancheAges) return;
        setSelectedTrancheAges(trancheAges.filter(f => f.actif).map(m => {
            return m.isAdulte && (m.nombrePersonnes || 0) <= 0 ? ({...m, nombrePersonnes: 1}) : m
        }));
    }, [trancheAges]);
    const closeButtonOption = useMemo(() => {
        return {
            text: "Annuler",
            onClick: onClose
        }
    }, [onClose])
    const reserverButtonOption = useMemo(() => {
        return {
            text: "Réserver",
            onClick: () => {
                onReserver({
                    longueurEquipement: longueurEquipement,
                    idTypeEquipement: selectedIdEquipement,
                    tranchesAges: selectedTrancheAges || [],
                    unites: selectedCell,
                    nombreAnimaux: nombreAnimaux,
                    idPlanAnimaux: animaux[0]?.idPlanEntete || Guid.empty
                })
                onClose()
            }
        }
    }, [onReserver, onClose, longueurEquipement, selectedIdEquipement, selectedCell, nombreAnimaux, animaux, selectedTrancheAges])

    if (!isPopupOpen) return null;
    return (
        <Popup title={"Information de réservation"} dragEnabled={false} visible={isPopupOpen} onHiding={onClose}
               width={400} height={400 + (trancheAges?.length || 0) * 10}>
            <Position
                my="center"
                collision="fit"
            />
            <div style={{display: "grid", gridRowGap: "0.3rem", gridTemplateColumns: "1fr 1fr"}}>
                <span>Date d'arrivée :</span>
                <DateBox style={{flex: 1}} value={dateDebut} onValueChanged={(e) => {
                    if (!e.event) return;
                    setNombreNuitee(differenceInDays(dateFin, e.value));
                    setDateDebut(e.value)
                }}/>
                <span>Date de départ :</span>
                <DateBox style={{flex: 1}} value={dateFin} onValueChanged={(e) => {
                    if (!e.event) return;
                    setNombreNuitee(differenceInDays(dateDebut, e.value));
                    setDateFin(e.value)
                }}/>
                <span>Nombre de nuitées :</span>
                <NumberBox style={{flex: 1}} value={nombreNuitee} min={0} onValueChanged={(e) => {
                    if (!e.event) return;
                    setDateFin(add(dateDebut, {days: e.value}))
                    setNombreNuitee(e.value);
                }}/>
                <span style={{gridColumnEnd: "span 2", marginTop: "1rem"}}>Veuiller spécifier les âges des membres de la réservation</span>
                {selectedTrancheAges?.map(trancheAge => {
                    return <TrancheAge key={`${trancheAge.idTrancheAge}`} trancheAge={trancheAge} onChange={(e) => {
                        setSelectedTrancheAges(prev => {
                            return prev?.map(p => {
                                if (!p.idTrancheAge.equals(trancheAge.idTrancheAge)) return p;
                                return {...p, nombrePersonnes: e.value}
                            });
                        })
                    }}/>
                })}
                {animaux && animaux.length > 0 ? <>
                    <span>{animaux[0].description}</span>
                    <NumberBox style={{flex: 1}} value={nombreAnimaux} min={0} onValueChanged={(e) => {
                        setNombreAnimaux(e.value);
                    }}/>
                </> : null}
                <span>Type d'équipement</span>
                <SelectBox dataSource={equipement} value={selectedIdEquipement} onValueChanged={(e) => {
                    setSelectedIdEquipement(e.value)
                }} displayExpr={"description"} valueExpr={"id"}/>
                <span>Longueur de l'équipement :</span>
                <NumberBox style={{flex: 1}} value={longueurEquipement} min={0} onValueChanged={(e) => {
                    setLongueurEquipement(e.value);
                }}/>
            </div>
            <ToolbarItem
                widget="dxButton"
                toolbar="bottom"
                location="before"
                options={reserverButtonOption}
            />
            <ToolbarItem
                widget="dxButton"
                toolbar="bottom"
                location="after"
                options={closeButtonOption}
            />
        </Popup>
    )
        ;
}

export default PopupReserver;